import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, ArrowRight, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscription Success Page
 * 
 * Displayed after successful Stripe checkout.
 * Polls for payment status and shows confirmation.
 */
export default function SubscriptionSuccessPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('checking');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const data = await response.json();

      if (data.payment_status === 'paid' || data.status === 'already_processed') {
        setStatus('success');
        setSubscriptionData(data);
        toast.success(data.message || 'Subscription activated!');
        return;
      }

      if (data.status === 'expired' || data.error) {
        setStatus('error');
        toast.error('Payment session expired or failed');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
      
    } catch (error) {
      console.error('Error checking status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  }, [sessionId, token, attempts, maxAttempts]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      <motion.div
        className="max-w-md w-full bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-8 text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 rounded-full bg-[var(--color-primary)]/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[var(--color-primary)] animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
            <p className="text-[var(--text-secondary)]">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div 
              className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 10 }}
            >
              <CheckCircle className="w-12 h-12 text-green-400" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome to {subscriptionData?.tier_name}!</h1>
            <p className="text-[var(--text-secondary)] mb-6">
              {subscriptionData?.message || 'Your subscription has been activated successfully.'}
            </p>
            
            <div className="bg-[var(--bg-tertiary)] rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-[var(--color-primary)]">
                <Crown className="w-5 h-5" />
                <span className="font-semibold">{subscriptionData?.tier_name} Plan Active</span>
              </div>
              {subscriptionData?.period_end && (
                <p className="text-sm text-[var(--text-tertiary)] mt-2">
                  Valid until {new Date(subscriptionData.period_end).toLocaleDateString()}
                </p>
              )}
            </div>

            <button
              onClick={() => navigate('/chat')}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-all"
            >
              Start Using AIRA
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Failed</h1>
            <p className="text-[var(--text-secondary)] mb-6">
              Something went wrong with your payment. Please try again.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              className="w-full py-3 px-4 rounded-xl bg-[var(--color-primary)] text-white font-semibold"
            >
              Try Again
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">⏳</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Pending</h1>
            <p className="text-[var(--text-secondary)] mb-6">
              Your payment is being processed. Please check your email for confirmation.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="w-full py-3 px-4 rounded-xl bg-[var(--color-primary)] text-white font-semibold"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
